import { Vector2 } from "../../common/vector2.js";
import { UIElement } from "./ui_element.js";
import { Game } from "../../game.js";

export interface IHighlightRect
{
  x: number;
  y: number;
  width: number;
  height: number;
}

export abstract class AbstractTutorialHighlight extends UIElement
{
  private static readonly DIM_OPACITY: number = 0.82;
  private static readonly OUTER_PADDING_RATIO: number = 0.4;

  private cutout: IHighlightRect | null = null;
  private radius_scale: number;

  constructor( ctx: CanvasRenderingContext2D, width: number, height: number )
  {
    super( ctx, 0, 0, width, height, false, false );

    this.radius_scale = this.getDefaultRadiusScale();

    this.setClickable( false );
    this.setOpacityImmediate( 0 );
  }

  protected abstract getDefaultRadiusScale(): number;

  public abstract name(): string;

  public showRect( rect: IHighlightRect, radius_scale: number = this.getDefaultRadiusScale() ): void
  {
    this.cutout = rect;
    this.radius_scale = radius_scale;
    this.forceRedraw();
    this.showElement();
  }

  public getCutoutCenter(): Vector2 | null
  {
    if ( !this.cutout )
      return null;

    return new Vector2(
      this.cutout.x + this.cutout.width / 2,
      this.cutout.y + this.cutout.height / 2
    );
  }

  public isPointInCutout( point: Vector2 ): boolean
  {
    const center = this.getCutoutCenter();
    if ( !center )
      return false;

    return point.distanceTo( center ) <= this.getCutoutRadius();
  }

  public hide(): void
  {
    this.hideElement();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
    {
      this.skipRenderSubtree();
      return;
    }

    this.startRender();

    const ctx = this.ctx;
    const w2 = this.rect.getW2();
    const h2 = this.rect.getH2();

    ctx.beginPath();
    ctx.fillStyle = `rgba( 0, 0, 0, ${AbstractTutorialHighlight.DIM_OPACITY} )`;
    ctx.rect(
      -w2 - Game.safeAreaLeft(),
      -h2 - Game.safeAreaTop(),
      Game.screenW(),
      Game.screenH()
    );
    ctx.fill();

    if ( this.cutout )
      this.drawSoftCutout( w2, h2 );

    this.finishRender();
  }

  private getCutoutRadius(): number
  {
    const cutout = this.cutout!;
    return Math.hypot( cutout.width / 2, cutout.height / 2 ) * this.radius_scale;
  }

  private drawSoftCutout( w2: number, h2: number ): void
  {
    const ctx = this.ctx;
    const cutout = this.cutout!;

    const half_w = cutout.width / 2;
    const half_h = cutout.height / 2;
    const target_radius = this.getCutoutRadius();
    const outer_radius = target_radius * ( 1 + AbstractTutorialHighlight.OUTER_PADDING_RATIO );
    const inner_stop = 1 / ( 1 + AbstractTutorialHighlight.OUTER_PADDING_RATIO );

    const cx = cutout.x + half_w - w2;
    const cy = cutout.y + half_h - h2;

    ctx.save();
    ctx.translate( cx, cy );

    const gradient = ctx.createRadialGradient( 0, 0, 0, 0, 0, outer_radius );
    gradient.addColorStop( 0, "rgba( 0, 0, 0, 1 )" );
    gradient.addColorStop( inner_stop, "rgba( 0, 0, 0, 1 )" );
    gradient.addColorStop( 1, "rgba( 0, 0, 0, 0 )" );

    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = gradient;
    ctx.fillRect( -outer_radius, -outer_radius, outer_radius * 2, outer_radius * 2 );
    ctx.globalCompositeOperation = "source-over";

    ctx.restore();
  }
}
